import {
  ETIQUETA_ESTADO_DE_PAGO,
  ETIQUETA_ESTADO_DE_PAGO_EN,
  ETIQUETA_ESTADO_FINANCIACION,
  ETIQUETA_ESTADO_FINANCIACION_EN,
  ETIQUETA_ESTADO_MOVIMIENTO,
  ETIQUETA_ESTADO_MOVIMIENTO_EN,
  ETIQUETA_ORIGEN_MOVIMIENTO,
  ETIQUETA_ORIGEN_MOVIMIENTO_EN,
  ETIQUETA_TIPO_DE_PAGO,
  ETIQUETA_TIPO_DE_PAGO_EN,
  type AcuerdoDePago,
  type EstadoDePago,
  type FinanciacionEnProceso,
  type Movimiento,
  type PagoATercero,
  type ResumenDeCashflow,
  type TipoDePago,
} from '@cnc/contracts';
import {et, tr, txt, type Texto} from '../i18n/idioma';
import {fecha, numero, textoOGuion} from '../utilidades/formato';
import {AYUDA_ACUERDOS, AYUDA_EN_PROCESO, AYUDA_MOVIMIENTOS, AYUDA_PAGOS, AYUDA_POR_EMPRESA} from './ayuda-cashflow';
import {type ColumnaDeTabla, type DefinicionDeColumnas} from './columnas';

// Columnas de las tablas de Cashflow (Daniel, 13/10/2026): se eligen, se
// arrastran y se ordenan como en el Consolidado, y la exportacion queda fija
// a la izquierda al moverse hacia los lados. `orden` es el nombre del dato:
// en Movimientos y Pagos ordena el servidor; en las demas, la pantalla.
const G = {
  principal: txt('Principal', 'Main'),
  fechas: txt('Fechas', 'Dates'),
  dinero: txt('Dinero', 'Money'),
  otros: txt('Otros', 'Other'),
};
const GRUPOS = [G.principal, G.fechas, G.dinero, G.otros];

type Col<T> = ColumnaDeTabla<T, string>;
const col = <T>(
  id: string,
  titulo: Col<T>['titulo'],
  grupo: Col<T>['grupo'],
  valor: (f: T) => string,
  // ayuda: la nota (i) del título: qué es la columna y cómo se llena.
  opciones: {porDefecto?: boolean; ancho?: number; numerica?: boolean; sinOrden?: boolean; ayuda?: Texto} = {},
): Col<T> => ({
  id,
  titulo,
  grupo,
  valor,
  orden: opciones.sinOrden ? undefined : id,
  porDefecto: opciones.porDefecto ?? true,
  ancho: opciones.ancho,
  numerica: opciones.numerica,
  ayuda: opciones.ayuda,
});
const AM = AYUDA_MOVIMIENTOS;
const AE = AYUDA_EN_PROCESO;
const AP = AYUDA_PAGOS;
const AA = AYUDA_ACUERDOS;
const AX = AYUDA_POR_EMPRESA;

// ── Movimientos (y "Vencido y próximos 30 días" del Resumen) ──
export const DEFINICION_MOVIMIENTOS: DefinicionDeColumnas<Movimiento, string> = {
  clave: 'cnc.cashflow.movimientos',
  fija: 'exportacion',
  grupos: GRUPOS,
  columnas: [
    col('exportacion', txt('Exportación', 'Export'), G.principal, x => x.exportacion, {
      ancho: 140,
      ayuda: AM.exportacion,
    }),
    col('fecha', txt('Fecha', 'Date'), G.fechas, x => fecha(x.fecha), {ancho: 110, ayuda: AM.fecha}),
    col(
      'estado',
      txt('Estado', 'Status'),
      G.principal,
      x => et(ETIQUETA_ESTADO_MOVIMIENTO, ETIQUETA_ESTADO_MOVIMIENTO_EN)[x.estado],
      {ancho: 110, ayuda: AM.estado},
    ),
    // Antes "Qué" (Daniel, 13/10/2026).
    col('concepto', txt('Tipo de movimiento', 'Movement type'), G.principal, x => x.concepto, {
      ancho: 220,
      ayuda: AM.concepto,
    }),
    col(
      'origen',
      txt('Origen', 'Source'),
      G.otros,
      x => et(ETIQUETA_ORIGEN_MOVIMIENTO, ETIQUETA_ORIGEN_MOVIMIENTO_EN)[x.origen],
      {porDefecto: false, ancho: 170, ayuda: AM.origen},
    ),
    col('paga', txt('Paga', 'Pays'), G.principal, x => textoOGuion(x.paga), {ancho: 170, ayuda: AM.paga}),
    col('recibe', txt('Recibe', 'Receives'), G.principal, x => textoOGuion(x.recibe), {
      ancho: 170,
      ayuda: AM.recibe,
    }),
    col('montoUsd', 'USD', G.dinero, x => numero(x.montoUsd), {numerica: true, ancho: 130, ayuda: AM.montoUsd}),
    col('linea', txt('Línea', 'Line'), G.dinero, x => textoOGuion(x.linea), {ancho: 90, ayuda: AM.linea}),
  ],
};

// ── En proceso: lo que se está financiando ──
export const DEFINICION_EN_PROCESO: DefinicionDeColumnas<FinanciacionEnProceso, string> = {
  clave: 'cnc.cashflow.en-proceso',
  fija: 'exportacion',
  grupos: GRUPOS,
  columnas: [
    col('exportacion', txt('Exportación', 'Export'), G.principal, x => x.exportacion, {
      ancho: 140,
      ayuda: AE.exportacion,
    }),
    col('linea', txt('Línea', 'Line'), G.principal, x => x.linea, {ancho: 80, ayuda: AE.linea}),
    col(
      'estado',
      txt('Etapa', 'Stage'),
      G.principal,
      x => et(ETIQUETA_ESTADO_FINANCIACION, ETIQUETA_ESTADO_FINANCIACION_EN)[x.estado],
      {ancho: 230, ayuda: AE.estado},
    ),
    col('financiadoUsd', txt('Financiado USD', 'Financed USD'), G.dinero, x => numero(x.financiadoUsd), {
      numerica: true,
      ancho: 140,
      ayuda: AE.financiadoUsd,
    }),
    col('recibe', txt('Recibió el dinero', 'Received the money'), G.principal, x => textoOGuion(x.recibe), {
      ancho: 170,
      ayuda: AE.recibe,
    }),
    col('desembolso', txt('Desembolso', 'Disbursement'), G.fechas, x => fecha(x.desembolso), {
      ancho: 150,
      ayuda: AE.desembolso,
    }),
    col(
      'diasFinanciado',
      txt('Días', 'Days'),
      G.fechas,
      x => (x.diasFinanciado === null ? '—' : numero(x.diasFinanciado)),
      {numerica: true, ancho: 70, ayuda: AE.diasFinanciado},
    ),
    col('comprador', txt('Le paga a la línea', 'Pays the line'), G.principal, x => textoOGuion(x.comprador), {
      ancho: 170,
      ayuda: AE.comprador,
    }),
    col('pagoComprador', txt('Paga el', 'Pays on'), G.fechas, x => fecha(x.pagoComprador), {
      ancho: 130,
      ayuda: AE.pagoComprador,
    }),
    col(
      'pendienteTercerosUsd',
      txt('Falta a terceros', 'Due to third parties'),
      G.dinero,
      x => (x.pendienteTercerosUsd ? numero(x.pendienteTercerosUsd) : '—'),
      {numerica: true, ancho: 150, ayuda: AE.pendienteTercerosUsd},
    ),
  ],
};

// ── Pagos a terceros ──
export const DEFINICION_PAGOS: DefinicionDeColumnas<PagoATercero, string> = {
  clave: 'cnc.cashflow.pagos',
  fija: 'exportacion',
  grupos: GRUPOS,
  columnas: [
    col('exportacion', txt('Exportación', 'Export'), G.principal, x => x.exportacion, {
      ancho: 140,
      ayuda: AP.exportacion,
    }),
    col('comprador', txt('Paga', 'Pays'), G.principal, x => textoOGuion(x.comprador), {
      ancho: 160,
      ayuda: AP.comprador,
    }),
    col('proveedor', txt('Proveedor', 'Supplier'), G.principal, x => textoOGuion(x.proveedor), {
      ancho: 180,
      ayuda: AP.proveedor,
    }),
    col(
      'tipo',
      txt('Tipo', 'Type'),
      G.principal,
      x => et(ETIQUETA_TIPO_DE_PAGO, ETIQUETA_TIPO_DE_PAGO_EN)[x.tipo as TipoDePago] ?? x.tipo,
      {ancho: 130, ayuda: AP.tipo},
    ),
    col('montoUsd', 'USD', G.dinero, x => numero(x.montoUsd), {numerica: true, ancho: 120, ayuda: AP.montoUsd}),
    col('llegada', txt('Llegada', 'Arrival'), G.fechas, x => fecha(x.llegada), {ancho: 110, ayuda: AP.llegada}),
    col('fechaMeta', txt('Fecha meta', 'Target date'), G.fechas, x => fecha(x.fechaMeta), {
      ancho: 160,
      ayuda: AP.fechaMeta,
    }),
    col(
      'estado',
      txt('Estado', 'Status'),
      G.principal,
      x => et(ETIQUETA_ESTADO_DE_PAGO, ETIQUETA_ESTADO_DE_PAGO_EN)[x.estado as EstadoDePago] ?? x.estado,
      {ancho: 120, ayuda: AP.estado},
    ),
    col('pagadoEl', txt('Pagado el', 'Paid on'), G.fechas, x => fecha(x.pagadoEl), {ancho: 110, ayuda: AP.pagadoEl}),
    col('linea', txt('Línea', 'Line'), G.dinero, x => textoOGuion(x.linea), {ancho: 120, ayuda: AP.linea}),
    col('acuerdo', txt('Acuerdo', 'Agreement'), G.otros, x => textoOGuion(x.acuerdo), {
      porDefecto: false,
      ancho: 120,
      ayuda: AP.acuerdo,
    }),
    col('comentarios', txt('Comentarios', 'Comments'), G.otros, x => x.comentarios ?? '', {
      ancho: 220,
      ayuda: AP.comentarios,
    }),
    col('acciones', txt('Acciones', 'Actions'), G.otros, () => '', {sinOrden: true, ancho: 170, ayuda: AP.acciones}),
  ],
};

// ── Acuerdos ──
const porcentaje = (v: number | null) => (v === null ? '—' : `${numero(v * 100)} %`);
export const plazoDeAcuerdo = (a: AcuerdoDePago) =>
  [
    a.terminosAnticipo,
    a.dias !== null && a.tipoDeDias
      ? tr(
          `(${a.dias} días ${a.tipoDeDias === 'business' ? 'hábiles' : 'calendario'})`,
          `(${a.dias} ${a.tipoDeDias} days)`,
        )
      : null,
  ]
    .filter(Boolean)
    .join(' ') || '—';
export const DEFINICION_ACUERDOS: DefinicionDeColumnas<AcuerdoDePago, string> = {
  clave: 'cnc.cashflow.acuerdos',
  fija: 'codigo',
  grupos: GRUPOS,
  columnas: [
    col('codigo', txt('Acuerdo', 'Agreement'), G.principal, a => a.codigo, {ancho: 120, ayuda: AA.codigo}),
    col('nombre', txt('Nombre', 'Name'), G.principal, a => textoOGuion(a.nombre), {ancho: 200, ayuda: AA.nombre}),
    col('comprador', txt('Empresa que compra', 'Buying company'), G.principal, a => textoOGuion(a.comprador), {
      ancho: 170,
      ayuda: AA.comprador,
    }),
    col('porcentajeAnticipo', txt('Anticipo', 'Advance'), G.dinero, a => porcentaje(a.porcentajeAnticipo), {
      numerica: true,
      ancho: 90,
      ayuda: AA.porcentajeAnticipo,
    }),
    col('terminosAnticipo', txt('Cuándo', 'When'), G.fechas, plazoDeAcuerdo, {
      ancho: 220,
      ayuda: AA.terminosAnticipo,
    }),
    col('qp', 'QP', G.otros, a => textoOGuion(a.qp), {ancho: 120, ayuda: AA.qp}),
    col('terminosFinales', txt('Liquidación', 'Settlement'), G.otros, a => textoOGuion(a.terminosFinales), {
      ancho: 180,
      ayuda: AA.terminosFinales,
    }),
    col('garantia', txt('Garantía', 'Guarantee'), G.otros, a => textoOGuion(a.garantia), {
      ancho: 160,
      ayuda: AA.garantia,
    }),
    col('pagos', txt('Pagos', 'Payments'), G.dinero, a => numero(a.pagos), {
      numerica: true,
      ancho: 80,
      ayuda: AA.pagos,
    }),
    col('pendienteUsd', txt('Pendiente', 'Pending'), G.dinero, a => (a.pendienteUsd ? numero(a.pendienteUsd) : '—'), {
      numerica: true,
      ancho: 130,
      ayuda: AA.pendienteUsd,
    }),
    col('linea', txt('Línea', 'Line'), G.dinero, a => textoOGuion(a.linea), {ancho: 170, ayuda: AA.linea}),
  ],
};

// ── Lo que cada empresa tiene por recibir y por pagar ──
export type EmpresaDeCashflow = ResumenDeCashflow['empresas'][number] & {netoUsd: number};
export const DEFINICION_POR_EMPRESA: DefinicionDeColumnas<EmpresaDeCashflow, string> = {
  clave: 'cnc.cashflow.por-empresa',
  fija: 'empresa',
  grupos: GRUPOS,
  columnas: [
    col('empresa', txt('Empresa', 'Company'), G.principal, e => e.empresa, {ancho: 220, ayuda: AX.empresa}),
    col('entradasUsd', txt('Por recibir', 'To receive'), G.dinero, e => numero(e.entradasUsd), {
      numerica: true,
      ancho: 140,
      ayuda: AX.entradasUsd,
    }),
    col(
      'vencidoEntradasUsd',
      txt('de eso, vencido', 'of which overdue'),
      G.dinero,
      e => (e.vencidoEntradasUsd ? numero(e.vencidoEntradasUsd) : '—'),
      {numerica: true, ancho: 140, ayuda: AX.vencidoEntradasUsd},
    ),
    col('salidasUsd', txt('Por pagar', 'To pay'), G.dinero, e => numero(e.salidasUsd), {
      numerica: true,
      ancho: 140,
      ayuda: AX.salidasUsd,
    }),
    col(
      'vencidoSalidasUsd',
      txt('de eso, vencido', 'of which overdue'),
      G.dinero,
      e => (e.vencidoSalidasUsd ? numero(e.vencidoSalidasUsd) : '—'),
      {numerica: true, ancho: 140, ayuda: AX.vencidoSalidasUsd},
    ),
    col('netoUsd', txt('Neto', 'Net'), G.dinero, e => `${e.netoUsd < 0 ? '−' : '+'}${numero(Math.abs(e.netoUsd))}`, {
      numerica: true,
      ancho: 140,
      ayuda: AX.netoUsd,
    }),
  ],
};

// Las definiciones que guarda una vista de Cashflow, con su nombre corto.
export const TABLAS_DE_CASHFLOW = {
  movimientos: DEFINICION_MOVIMIENTOS,
  enProceso: DEFINICION_EN_PROCESO,
  pagos: DEFINICION_PAGOS,
  acuerdos: DEFINICION_ACUERDOS,
  porEmpresa: DEFINICION_POR_EMPRESA,
} as const;
export type TablaDeCashflow = keyof typeof TABLAS_DE_CASHFLOW;

// Orden en la pantalla: por el dato de la columna; vacios al final; numeros como numeros.
export function ordenarFilas<T extends object>(filas: T[], orden: string, direccion: 'asc' | 'desc'): T[] {
  if (!orden) return filas;
  const signo = direccion === 'desc' ? -1 : 1;
  const valor = (x: T) => (x as Record<string, unknown>)[orden];
  const vacio = (v: unknown) => v === null || v === undefined || v === '';
  return [...filas].sort((x, y) => {
    const a = valor(x);
    const b = valor(y);
    if (vacio(a)) return vacio(b) ? 0 : 1;
    if (vacio(b)) return -1;
    const r =
      typeof a === 'number' && typeof b === 'number'
        ? a - b
        : String(a).localeCompare(String(b), 'es', {numeric: true});
    return r * signo;
  });
}
